import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table } from 'react-bootstrap';
import moment from 'moment';

export const ProfitAndLossReport = ({ pnl, startDate, endDate, activeProject, accounts, formatCurrency }) => {
  const getCleanName = (name) => {
    return name.replace(/^[\s\u00A0]+/, '').replace(/^\d+\s*·\s*/, '');
  };

  const getAccountType = (name) => {
    const clean = getCleanName(name);
    const acc = accounts.find(a => a.name === clean);
    return acc ? acc.type : '';
  };

  const getIndentClass = (name, baseIndent = 3) => {
    const match = name.match(/^[\s\u00A0]+/);
    const spaces = match ? match[0].length : 0;
    const level = Math.floor(spaces / 2);
    return `pl-${Math.min(baseIndent + level, 6)}`;
  };

  const renderAccountNameWithLink = (name, indentClass = "pl-3") => {
    const cleanName = getCleanName(name);
    const targetAcc = accounts.find(a => a.name === cleanName);
    return (
      <td className={indentClass}>
        {targetAcc ? (
          <Link to={`/accounts/${targetAcc._id}/transactions`} className="text-decoration-none text-muted font-weight-bold" style={{ cursor: 'pointer' }}>
            {name}
          </Link>
        ) : (
          name
        )}
      </td>
    );
  };

  const renderAmountWithLink = (name, amount, textClass = "text-dark") => {
    const cleanName = getCleanName(name);
    const targetAcc = accounts.find(a => a.name === cleanName);
    return (
      <td className={`text-right ${textClass}`}>
        {targetAcc ? (
          <Link to={`/accounts/${targetAcc._id}/transactions`} className="text-decoration-none text-muted font-weight-bold" style={{ cursor: 'pointer' }}>
            {formatCurrency(amount)}
          </Link>
        ) : (
          formatCurrency(amount)
        )}
      </td>
    );
  };

  // Partition data
  const incomeDetails = pnl.details?.income || {};
  const expenseDetails = pnl.details?.expenses || {};
  const cogsDetails = pnl.details?.cogs || {};

  const ordinaryIncomeRows = [];
  const otherIncomeRows = [];
  let totalOrdinaryIncome = 0;
  let totalOtherIncome = 0;

  Object.entries(incomeDetails).forEach(([name, amount]) => {
    const type = getAccountType(name);
    if (type === 'Other Income') {
      otherIncomeRows.push([name, amount]);
      otherIncomeRows.push(...(name.match(/^[\s\u00A0]+/) ? [] : [[name, amount]])); // If not sub-account, sum up
    } else {
      ordinaryIncomeRows.push([name, amount]);
    }
  });

  // Calculate distinct sums for ordinary vs other income (safeguard)
  ordinaryIncomeRows.forEach(([name, amount]) => {
    const clean = getCleanName(name);
    const acc = accounts.find(a => a.name === clean);
    if (acc && !acc.parentId) totalOrdinaryIncome += amount;
  });
  otherIncomeRows.forEach(([name, amount]) => {
    const clean = getCleanName(name);
    const acc = accounts.find(a => a.name === clean);
    if (acc && !acc.parentId) totalOtherIncome += amount;
  });

  const ordinaryExpenseRows = [];
  const otherExpenseRows = [];
  let totalOrdinaryExpenses = 0;
  let totalOtherExpenses = 0;

  Object.entries(expenseDetails).forEach(([name, amount]) => {
    const type = getAccountType(name);
    if (type === 'Other Expense') {
      otherExpenseRows.push([name, amount]);
    } else {
      ordinaryExpenseRows.push([name, amount]);
    }
  });

  ordinaryExpenseRows.forEach(([name, amount]) => {
    const clean = getCleanName(name);
    const acc = accounts.find(a => a.name === clean);
    if (acc && !acc.parentId) totalOrdinaryExpenses += amount;
  });
  otherExpenseRows.forEach(([name, amount]) => {
    const clean = getCleanName(name);
    const acc = accounts.find(a => a.name === clean);
    if (acc && !acc.parentId) totalOtherExpenses += amount;
  });

  const totalCOGS = pnl.totalCOGS || 0;
  const netOrdinaryIncome = totalOrdinaryIncome - totalCOGS - totalOrdinaryExpenses;

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-4">
        <div className="text-center mb-4">
          <h4 className="mb-1">Profit and Loss</h4>
          <div className="text-muted small">{activeProject.name}</div>
          <div className="text-muted extra-small">For the period from {moment(startDate).format('MM/DD/YYYY')} to {moment(endDate).format('MM/DD/YYYY')}</div>
        </div>

        <Table responsive borderless className="report-table mb-0">
          <tbody>
            {/* Ordinary Income/Expense Section */}
            <tr className="bg-light font-weight-bold"><td colSpan="2" className="text-uppercase text-primary pl-1">Ordinary Income/Expense</td></tr>

            {/* Income */}
            <tr className="font-weight-bold small"><td className="pl-3">Income</td><td></td></tr>
            {ordinaryIncomeRows.map(([name, amount]) => (
              <tr key={name} className="extra-small">
                {renderAccountNameWithLink(name, getIndentClass(name, 4))}
                {renderAmountWithLink(name, amount, "text-dark")}
              </tr>
            ))}
            <tr className="font-weight-bold small"><td className="pl-3">Total Income</td><td className="text-right border-top border-dark">{formatCurrency(totalOrdinaryIncome)}</td></tr>

            {/* COGS (if any) */}
            {totalCOGS > 0 && (
              <>
                <tr className="font-weight-bold small mt-2"><td className="pl-3">Cost of Goods Sold</td><td></td></tr>
                {Object.entries(cogsDetails).map(([name, amount]) => (
                  <tr key={name} className="extra-small">
                    {renderAccountNameWithLink(name, getIndentClass(name, 4))}
                    {renderAmountWithLink(name, amount, "text-danger")}
                  </tr>
                ))}
                <tr className="font-weight-bold small"><td className="pl-3">Total COGS</td><td className="text-right border-top">{formatCurrency(totalCOGS)}</td></tr>
              </>
            )}

            <tr className="font-weight-bold small"><td className="pl-3">Gross Profit</td><td className="text-right border-top border-dark">{formatCurrency(totalOrdinaryIncome - totalCOGS)}</td></tr>

            {/* Expenses */}
            <tr className="font-weight-bold small mt-2"><td className="pl-3">Expense</td><td></td></tr>
            {ordinaryExpenseRows.map(([name, amount]) => (
              <tr key={name} className="extra-small">
                {renderAccountNameWithLink(name, getIndentClass(name, 4))}
                {renderAmountWithLink(name, amount, "text-dark")}
              </tr>
            ))}
            <tr className="font-weight-bold small"><td className="pl-3">Total Expense</td><td className="text-right border-top border-dark">{formatCurrency(totalOrdinaryExpenses)}</td></tr>

            <tr className="font-weight-bold small"><td className="pl-3">Net Ordinary Income</td><td className="text-right border-top border-dark">{formatCurrency(netOrdinaryIncome)}</td></tr>

            {/* Other Income/Expense Section */}
            {(otherIncomeRows.length > 0 || otherExpenseRows.length > 0) && (
              <>
                <tr className="bg-light font-weight-bold mt-3"><td colSpan="2" className="text-uppercase text-primary pl-1">Other Income/Expense</td></tr>
                
                {/* Other Income */}
                {otherIncomeRows.length > 0 && (
                  <>
                    <tr className="font-weight-bold small"><td className="pl-3">Other Income</td><td></td></tr>
                    {otherIncomeRows.map(([name, amount]) => (
                      <tr key={name} className="extra-small">
                        {renderAccountNameWithLink(name, getIndentClass(name, 4))}
                        {renderAmountWithLink(name, amount, "text-success")}
                      </tr>
                    ))}
                    <tr className="font-weight-bold small"><td className="pl-3">Total Other Income</td><td className="text-right border-top">{formatCurrency(totalOtherIncome)}</td></tr>
                  </>
                )}

                {/* Other Expense */}
                {otherExpenseRows.length > 0 && (
                  <>
                    <tr className="font-weight-bold small mt-2"><td className="pl-3">Other Expense</td><td></td></tr>
                    {otherExpenseRows.map(([name, amount]) => (
                      <tr key={name} className="extra-small">
                        {renderAccountNameWithLink(name, getIndentClass(name, 4))}
                        {renderAmountWithLink(name, amount, "text-danger")}
                      </tr>
                    ))}
                    <tr className="font-weight-bold small"><td className="pl-3">Total Other Expense</td><td className="text-right border-top">{formatCurrency(totalOtherExpenses)}</td></tr>
                  </>
                )}

                <tr className="font-weight-bold small"><td className="pl-3">Net Other Income</td><td className="text-right border-top border-dark">{formatCurrency(totalOtherIncome - totalOtherExpenses)}</td></tr>
              </>
            )}

            {/* Net Income */}
            <tr className="h5 font-weight-bold border-top border-bottom border-dark bg-dark text-white">
              <td className="pl-1">NET INCOME</td>
              <td className="text-right">{formatCurrency(pnl.netIncome)}</td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};
